import { getEnhancementPoints } from './list/points.js';
import { UnitBlightedWildData } from './list/ptg/IBlightedWildsData.js';
import { UnitRavagedCoastData } from './list/ptg/IRavagedCoastData.js';
import { PtgBattlePack } from './models/IBattlePack.js';
import { WeaponType } from './models/IWeapon.js';
import { hasValidator, ptgAllowEnhancementsValidators, ptgAllowLoresValidators, ptgAllowPathsValidators, } from './list/validation/validators.js';
import { getScourgeType, nameToScourgeType, ScourgeType } from './models/IScourge.js';
// Convert a string toCamelCase
export const toCamelCase = (str) => {
    return str
        .toLowerCase()
        .split(' ')
        .map((word, index) => (index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)))
        .join('');
};
export function joinWithAnd(items) {
    if (items.length === 0)
        return '';
    if (items.length === 1)
        return items[0];
    if (items.length === 2)
        return `${items[0]} and ${items[1]}`;
    return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}
export const safeName = (a) => {
    return a.toLocaleLowerCase().replace(/[^a-z0-9]/g, '');
};
export const namesEqual = (a, b) => {
    if (a === null || b === null)
        return false;
    const left = safeName(a);
    const right = safeName(b);
    return left === right;
};
export function unitDataFactory(battlePack) {
    if (battlePack === PtgBattlePack.RavagedCoast) {
        return new UnitRavagedCoastData();
    }
    else if (battlePack === PtgBattlePack.BlightedWilds) {
        return new UnitBlightedWildData();
    }
    return undefined;
}
export function getGrandAlliance(alliance, armies) {
    return Object.values(armies).filter((a) => a.alliance === alliance && !a.isArmyOfRenown);
}
export function getUnitData(listUnit, army, includeRoRUnits = false, ptg) {
    if (listUnit.anvilUnit?.unit) {
        return listUnit.anvilUnit.unit;
    }
    if (includeRoRUnits && ptg) {
        const rorUnit = getUnitDataFromPtgRegimentOfRenown(listUnit.name, ptg);
        if (rorUnit)
            return rorUnit;
    }
    return getUnitFromSet(listUnit, army, includeRoRUnits);
}
export function getUnit(name, army, searchRoR = false, ptg) {
    const unit = army.units.find((u) => namesEqual(u.name, name));
    if (unit)
        return unit;
    if (!searchRoR)
        return undefined;
    if (ptg) {
        const rorUnit = getUnitDataFromPtgRegimentOfRenown(name, ptg);
        if (rorUnit)
            return rorUnit;
    }
    // check all RoRs
    for (const ror of army.regimentsOfRenown) {
        if (ror.units) {
            const rorUnit = ror.units.find((rorU) => namesEqual(rorU.unit.name, name));
            if (rorUnit)
                return rorUnit.unit;
        }
    }
    return undefined;
}
export function getUnitFromGrandAlliance(listUnit, grandAlliance, ptg) {
    if (listUnit.anvilUnit?.unit) {
        return listUnit.anvilUnit.unit;
    }
    if (ptg) {
        const rorUnit = getUnitDataFromPtgRegimentOfRenown(listUnit.name, ptg);
        if (rorUnit)
            return rorUnit;
    }
    const faction = listUnit.faction;
    if (faction) {
        const army = grandAlliance.find((a) => namesEqual(a.name, faction));
        if (army)
            return army.units.find((u) => namesEqual(u.name, listUnit.name));
    }
    let unit;
    grandAlliance.some((faction) => {
        unit = faction.units.find((u) => namesEqual(u.name, listUnit.name));
        return unit ? true : false;
    });
    return unit;
}
export function getUnitFromSet(listUnit, armyOrGrandAlliance, includeRoRUnits = false, ptg) {
    if (listUnit.anvilUnit?.unit) {
        return listUnit.anvilUnit.unit;
    }
    // Allows access to a unit from either a single army or a set of armies
    // useful for code-reuse for both matched play and ptg which can allow
    // unit selection from the entire grand alliance
    if (Array.isArray(armyOrGrandAlliance)) {
        return getUnitFromGrandAlliance(listUnit, armyOrGrandAlliance, ptg);
    }
    return getUnit(listUnit.name, armyOrGrandAlliance, includeRoRUnits, ptg);
}
export function getUnitArmy(unit, armies, army) {
    if (unit.faction) {
        return armies[unit.faction];
    }
    if (army?.units.some((u) => namesEqual(u.name, unit.name))) {
        return army;
    }
    const grandAlliance = army ? getGrandAlliance(army.alliance, armies) : Object.values(armies);
    return grandAlliance.find((a) => a.units.some((u) => namesEqual(unit.name, u.name)));
}
export function getBattleTacticCards(battlepack, data) {
    if (data.generalsHandbook) {
        const ghb = data.generalsHandbook.find((g) => g.battlePack === battlepack);
        if (ghb?.battleTacticCards) {
            return ghb.battleTacticCards;
        }
    }
    if (data.battleTacticCards) {
        return data.battleTacticCards;
    }
    return [];
}
export function getUnitPtgRegimentOfRenown(unitName, ptg) {
    // unit name that is expected to exist in a regiment of renown from a path to glory battlepack
    return ptg.regimentsOfRenown.find((ror) => ror.units.some((u) => namesEqual(u.unit.name, unitName)));
}
export function getUnitDataFromPtgRegimentOfRenown(unitName, ptg) {
    const ror = getUnitPtgRegimentOfRenown(unitName, ptg);
    if (!ror)
        return undefined;
    const rorUnit = ror.units.find((u) => namesEqual(u.unit.name, unitName));
    return rorUnit?.unit;
}
export function getPath(pathName, ptgBattlePack, ptgCampaigns, morePaths, 
// yea this is slapped on here and this api is poop
validators) {
    let path = morePaths?.find((p) => p.name === pathName);
    if (path)
        return path;
    if (ptgBattlePack === PtgBattlePack.BlightedWilds) {
        // take anything
        ptgCampaigns.some((ptg) => {
            path = ptg?.paths.find((p) => p.name === pathName);
            return path !== undefined;
        });
        if (path)
            return path;
    }
    const battlepack = ptgCampaigns.find((ptg) => ptg.battlePack === PtgBattlePack.RavagedCoast);
    path = battlepack?.paths.find((p) => p.name === pathName);
    if (path)
        return path;
    ptgAllowPathsValidators.some((validator) => {
        if (hasValidator(validator.id, validators ?? [])) {
            const ptgBp = ptgCampaigns.find((p) => p.battlePack === validator.battlepack);
            path = ptgBp?.paths.find((p) => p.name === pathName);
        }
        return path !== undefined;
    });
    return path;
}
// Get all of the lores available for a given ptg list
export const getAvailablePtgLores = (loreType, list, scrolldexData) => {
    const ptg = scrolldexData.pathToGlory.find((ptg) => ptg.battlePack === list.battlePack);
    let ptgLore = ptg?.lores[loreType] ?? [];
    ptgAllowLoresValidators.forEach((validator) => {
        if (hasValidator(validator.id, list.validators)) {
            const ptgBp = scrolldexData.pathToGlory.find((ptg) => ptg.battlePack === validator.battlepack);
            if (ptgBp?.lores[loreType]) {
                ptgLore = ptgLore.concat(ptgBp.lores[loreType]);
            }
        }
    });
    return ptgLore;
};
export function allUnits(list, includeRoRUnits = false) {
    const units = [];
    for (const regiment of list.regiments) {
        units.push(regiment.leader);
        units.push(...regiment.units);
    }
    units.push(...list.auxiliaryUnits);
    if (includeRoRUnits && list.regimentOfRenownUnits) {
        units.push(...list.regimentOfRenownUnits);
    }
    const bwListData = list.data;
    if (bwListData?.rog) {
        if (bwListData.rog.hero) {
            units.push(bwListData.rog.hero);
        }
        units.push(...bwListData.rog.units);
    }
    return units;
}
export function getEnhancementCount(unit) {
    let count = 0;
    if (unit.heroicTrait && unit.heroicTrait !== '')
        count += 1;
    if (unit.artifact && unit.artifact !== '')
        count += 1;
    count += unit.enhancementChoices ? Object.keys(unit.enhancementChoices).length : 0;
    if (unit.data && unit.data.emberstoneWeapon) {
        ++count;
    }
    return count;
}
export function getTotalEnhancementPoints(unit, army) {
    let points = 0;
    if (!army)
        return points;
    if (unit.heroicTrait) {
        points += getEnhancementPoints(unit.heroicTrait, army);
    }
    if (unit.artifact) {
        points += getEnhancementPoints(unit.artifact, army);
    }
    if (unit.enhancementChoices) {
        for (const [_, enhancement] of Object.entries(unit.enhancementChoices)) {
            points += getEnhancementPoints(enhancement, army);
        }
    }
    return points;
}
// Resolve a heroic trait / artifact / enhancement name to its full IUpgrade from the army's enhancement tables.
export function getEnhancementData(eName, army) {
    if (!eName)
        return undefined;
    let found;
    const groups = Object.entries(army.upgrades.enhancements).flatMap(([, group]) => group);
    groups.some((group) => group?.upgrades.some((upgrade) => {
        if (upgrade.name === eName) {
            found = upgrade;
            return true;
        }
        return false;
    }));
    return found;
}
export function getUnitsOfType(army, type) {
    return army.units.filter((u) => u.type === type);
}
export function matchesRulesContent(item, rulesContent) {
    if (rulesContent === 'both')
        return true;
    let type = ScourgeType.None;
    if (item.scourge !== undefined) {
        type = getScourgeType(item);
    }
    else if (item.name !== undefined) {
        type = nameToScourgeType(item.name);
    }
    if (rulesContent === 'ghb2025') {
        return type !== ScourgeType.SoA; // ghb 2026
    }
    if (rulesContent === 'latest') {
        return type !== ScourgeType.SoG; // gbh 2025
    }
    return true;
}
export function getArmy(faction, data) {
    const army = data.armies[faction];
    if (!army) {
        return Object.values(data.armies).find((a) => namesEqual(a.name, faction));
    }
    return army;
}
export function getRoR(name, data) {
    return data.regimentsOfRenown.find((ror) => namesEqual(ror.name, name));
}
export function getRoRUnit(name, ror) {
    return ror.units.find((rorU) => namesEqual(rorU.unit.name, name));
}
export function hasKeyword(keyword, keywords) {
    if (!keyword)
        return false;
    return keywords.some((k) => {
        // remove anything in paranetheses and trim whitespace
        const cleanedKeyword = k
            .replace(/\s*\(.*?\)\s*/g, '')
            .toLowerCase()
            .trim();
        return cleanedKeyword === keyword.toLowerCase();
    });
}
export const HEROIC_TRAITS_GROUP_NAME = 'Heroic Traits';
export const ARTIFACTS_GROUP_NAME = 'Artefacts of Power';
// Armies whose hero battle profiles don't carry the Heroic Traits / Artefacts of
// Power tables. In the BSData source the tables are added via conditions that our
// parser doesn't interpret, so the enhancements never make it onto the unit/army.
// In Path to Glory these armies can still take heroic traits / artefacts (provided
// by the campaign), so we allow them explicitly here.
export const PTG_ENHANCEMENT_ARMIES = ['Cities of Sigmar - The Iron March'];
export function armyAllowsPtgEnhancements(faction) {
    return PTG_ENHANCEMENT_ARMIES.some((name) => namesEqual(name, faction));
}
export function getEnhancementGroup(enhancements, groupName) {
    return Object.entries(enhancements).find(([name]) => name === groupName)?.[1];
}
export function getPtgEnhancementGroups(list, data, groupName) {
    const filterGroup = (enhancements) => {
        if (groupName)
            return getEnhancementGroup(enhancements, groupName) ?? [];
        return Object.values(enhancements).flatMap((g) => g);
    };
    const ptg = data.pathToGlory.find((ptg) => ptg.battlePack === list.battlePack);
    let eg = [];
    if (ptg) {
        eg = filterGroup(ptg.enhancements);
    }
    ptgAllowEnhancementsValidators.forEach((validator) => {
        if (hasValidator(validator.id, list.validators)) {
            const ptgBp = data.pathToGlory.find((p) => p.battlePack === validator.battlepack);
            if (ptgBp?.enhancements) {
                const ptgBpEg = filterGroup(ptgBp.enhancements);
                if (ptgBpEg)
                    eg = eg.concat(ptgBpEg);
            }
        }
    });
    return eg;
}
export function getOtherEnhancementGroups(enhancements) {
    const otherGroups = new Map();
    for (const [name, group] of Object.entries(enhancements)) {
        if (name !== HEROIC_TRAITS_GROUP_NAME && name !== ARTIFACTS_GROUP_NAME) {
            otherGroups.set(name, group);
        }
    }
    return otherGroups;
}
export function getAllWeaponProfiles(unit) {
    const melee = [];
    const ranged = [];
    unit.models.forEach((model) => {
        const selections = Object.values(model.weapons.advanced).concat(Object.values(model.weapons.basic));
        selections.forEach((s) => {
            s.weapons.forEach((w) => {
                if (w.type === WeaponType.Melee) {
                    melee.push(w);
                }
                else if (w.type === WeaponType.Ranged) {
                    ranged.push(w);
                }
            });
        });
        model.optionSets.forEach((set) => {
            set.options.forEach((option) => {
                option.weapons.forEach((w) => {
                    if (w.type === WeaponType.Melee) {
                        melee.push(w);
                    }
                    else if (w.type === WeaponType.Ranged) {
                        ranged.push(w);
                    }
                });
            });
        });
    });
    // deduplicate
    const meleeMap = new Map();
    melee.forEach((w) => {
        meleeMap.set(w.name, w);
    });
    const rangedMap = new Map();
    ranged.forEach((w) => {
        rangedMap.set(w.name, w);
    });
    return { melee: Array.from(meleeMap.values()), ranged: Array.from(rangedMap.values()) };
}
export function getDefaultWeaponOptions(unit) {
    const result = {};
    unit.models.forEach((model) => {
        // optional weapons (with max)
        const arr = [];
        Object.values(model.weapons.advanced).forEach((w) => {
            arr.push({ name: w.name, count: w.max });
        });
        const weaponSets = model.optionSets.filter((set) => set.options.some((option) => option.weapons.length > 0));
        weaponSets.forEach((set) => {
            // take the first weapon in the set
            arr.push({ name: set.options[0].name, count: model.max });
        });
        if (arr.length > 0)
            result[model.name] = arr;
    });
    return result;
}
export function getDefaultUpgrades(unit) {
    const upgrades = [];
    for (const set of unit.optionSets) {
        if (set.options.length > 0) {
            upgrades.push(set.options[0].name);
        }
    }
    return upgrades;
}
// Drop any stored weapon/upgrade selections that no longer exist on the unit.
// Needed because selections are persisted per unit name (in unit settings and on saved
// lists); if a book update renames or removes a weapon/option, the stale selection
// would surface as an unfixable validation error the next time it was applied.
export function sanitizeUnitSelections(unit, weapons, upgrades) {
    const cleanWeapons = {};
    if (weapons) {
        for (const model of unit.models) {
            const options = weapons[model.name];
            if (!options)
                continue;
            const validNames = new Set();
            Object.keys(model.weapons.advanced).forEach((name) => validNames.add(name));
            model.optionSets.forEach((set) => set.options.forEach((o) => validNames.add(o.name)));
            const filtered = options.filter((o) => validNames.has(o.name));
            if (filtered.length > 0)
                cleanWeapons[model.name] = filtered;
        }
    }
    const validUpgradeNames = new Set();
    unit.optionSets.forEach((set) => set.options.forEach((o) => validUpgradeNames.add(o.name)));
    const cleanUpgrades = (upgrades ?? []).filter((name) => validUpgradeNames.has(name));
    return { weapons: cleanWeapons, upgrades: cleanUpgrades };
}
export function hasOptions(unit) {
    if (unit.optionSets.length > 0)
        return true;
    return unit.models.some((model) => model.optionSets.length > 0) || hasWeaponOptions(unit);
}
export function hasWeaponOptions(unit) {
    if (unit.optionSets.some((set) => set.options.some((option) => option.weapons.length > 0))) {
        return true;
    }
    return unit.models.some((model) => {
        return (Object.values(model.weapons.advanced).length > 0 ||
            model.optionSets.some((set) => set.options.some((option) => option.weapons.length > 0)));
    });
}
export function getPathToGlory(list, data) {
    if (!list.battlePack)
        return undefined;
    return data.pathToGlory.find((ptg) => ptg.battlePack === list.battlePack);
}
